import {
  IPlatformAPI,
  IPlatformServices,
} from '@wix/native-components-infra/dist/src/types/types';
import { IHttpClient } from '@wix/http-client';
import { getEcommerceSettings } from '@wix/ambassador-ecom-v1-ecommerce-settings/http';
import {
  ActiveProvider,
  CheckoutBillingFields,
  CheckoutBrand,
  CheckoutFields,
  CheckoutPolicies,
  CountryDeliveryProperties,
  DeliveryDestinationProperties,
  GetEcommerceSettingsResponse,
  RequestedFields,
} from '@wix/ambassador-ecom-v1-ecommerce-settings/types';
import { hasRequestedFieldError } from './EcommerceSettingsService.utils';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { ViewerStorage } from '../viewer-storage/ViewerStorage';
import { EcomPlatformViewerScriptContext } from '../../viewer-script/types';
import { SPECS } from '../../constants';

const ECOMMERCE_SETTINGS_CACHE_KEY = 'SPI_CONFIG_PROPERTIES';
const ECOMMERCE_LOADING_STATE_SETTINGS_CACHE_KEY =
  'LOADING_STATE_SPI_CONFIG_PROPERTIES';

export interface LoadingStateEcommerceSettings {
  checkoutSettings?: {
    checkoutPolicies?: CheckoutPolicies;
    checkoutFields?: CheckoutFields;
    checkoutBrand?: CheckoutBrand;
    checkoutBillingFields?: CheckoutBillingFields;
  };
}

export interface EcommerceSettings extends LoadingStateEcommerceSettings {
  isPremium?: boolean;
  delivery: {
    countries?: CountryDeliveryProperties[];
    restOfWorld?: DeliveryDestinationProperties;
  };
  tax: {
    taxIncludedInItemPrices?: boolean;
    configured?: boolean;
  };
  businessInfo?: {
    storeName?: string | null;
    logo?: string | null;
    country?: string | null;
  };
  purchaseRecommendationsProviders?: ActiveProvider[];
}

export class EcommerceSettingsService {
  private readonly apis: IPlatformAPI;
  private readonly httpClient: IHttpClient;
  private readonly siteStore: SiteStore;
  private ecommerceSettingsPromise?: Promise<GetEcommerceSettingsResponse>;
  private readonly errorHandler: IPlatformServices['essentials']['errorHandler'];
  private readonly experiments: IPlatformServices['essentials']['experiments'];
  private readonly viewerStorage: ViewerStorage<GetEcommerceSettingsResponse>;

  constructor({
    context,
    isLightbox,
    apis,
    platformServices,
  }: {
    context: EcomPlatformViewerScriptContext;
    apis: IPlatformAPI;
    isLightbox: boolean;
    platformServices: IPlatformServices;
  }) {
    this.apis = apis;
    this.siteStore = context.siteStore;
    this.viewerStorage = new ViewerStorage({
      cacheKey: ECOMMERCE_SETTINGS_CACHE_KEY,
      clearOnRefresh: !isLightbox,
      storage: this.apis.storage,
      siteStore: this.siteStore,
    });
    this.httpClient = platformServices.essentials.httpClient;
    this.errorHandler = platformServices.essentials.errorHandler;
    this.experiments = platformServices.essentials.experiments;
  }

  public init() {
    this.ecommerceSettingsPromise =
      this.getEcommerceSettingsFromServerOrCache();
    this.ecommerceSettingsPromise.catch(() => {
      //
    });
  }

  public async getEcommerceSettings(): Promise<EcommerceSettings> {
    if (!this.ecommerceSettingsPromise) {
      this.ecommerceSettingsPromise =
        this.getEcommerceSettingsFromServerOrCache();
    }

    let ecommerceSettingsResponse;
    try {
      ecommerceSettingsResponse = await this.ecommerceSettingsPromise;
    } catch {
      ecommerceSettingsResponse = this.getAllFieldsFailedResponse();
    }

    if (
      !ecommerceSettingsResponse?.ecommerceSettings &&
      this.experiments.enabled(SPECS.ecomFallbackForMissingEcommerceSettings)
    ) {
      ecommerceSettingsResponse = this.getAllFieldsFailedResponse();
    }

    return {
      isPremium: !hasRequestedFieldError(
        ecommerceSettingsResponse.errors,
        RequestedFields.PREMIUM_SITE,
      )
        ? Boolean(ecommerceSettingsResponse.ecommerceSettings?.premiumSite)
        : true,
      delivery: this.getDeliveryConfiguration(ecommerceSettingsResponse),
      tax: {
        taxIncludedInItemPrices: Boolean(
          ecommerceSettingsResponse.ecommerceSettings?.tax
            ?.taxIncludedInItemPrices,
        ),
        configured: Boolean(
          ecommerceSettingsResponse.ecommerceSettings?.tax?.configured ?? true,
        ),
      },
      businessInfo: {
        storeName:
          ecommerceSettingsResponse.ecommerceSettings?.businessInfo?.name,
        logo: ecommerceSettingsResponse.ecommerceSettings?.businessInfo?.logo,
        country:
          ecommerceSettingsResponse.ecommerceSettings?.businessInfo?.address
            ?.country,
      },
      checkoutSettings: {
        ...ecommerceSettingsResponse.ecommerceSettings?.checkout,
      },
      purchaseRecommendationsProviders: this.experiments.enabled(
        SPECS.ecomPurchaseRecommendationsSettingsField,
      )
        ? !hasRequestedFieldError(
            ecommerceSettingsResponse.errors,
            RequestedFields.PURCHASE_RECOMMENDATIONS_SERVICE_PLUGIN,
          )
          ? ecommerceSettingsResponse.ecommerceSettings
              ?.purchaseRecommendationsServicePlugin?.activeProviders ?? []
          : []
        : [],
    };
  }

  public async getLoadingStateEcommerceSettings(): Promise<
    LoadingStateEcommerceSettings | undefined
  > {
    if (!this.siteStore.isSSR()) {
      const warmup = this.siteStore.windowApis.warmupData.get(
        ECOMMERCE_LOADING_STATE_SETTINGS_CACHE_KEY,
      ) as LoadingStateEcommerceSettings | undefined;

      if (warmup) {
        return warmup;
      }
    }

    let response;
    try {
      response = (
        await this.errorHandler.withErrorHandler(
          () => this.checkoutSettingsRequest(),
          { errorCodesMap: {} },
        )
      ).data;
    } catch {
      return undefined;
    }

    const loadingStateEcommerceSettings: LoadingStateEcommerceSettings = {
      checkoutSettings: { ...response.ecommerceSettings?.checkout },
    };

    if (this.siteStore.isSSR()) {
      this.siteStore.windowApis.warmupData.set(
        ECOMMERCE_LOADING_STATE_SETTINGS_CACHE_KEY,
        loadingStateEcommerceSettings,
      );
    }

    return loadingStateEcommerceSettings;
  }

  private getAllFieldsFailedResponse(): GetEcommerceSettingsResponse {
    return {
      ecommerceSettings: undefined,
      errors: [
        { requestedField: RequestedFields.DELIVERY },
        { requestedField: RequestedFields.BUSINESS_INFO },
        { requestedField: RequestedFields.CHECKOUT },
        { requestedField: RequestedFields.PREMIUM_SITE },
        { requestedField: RequestedFields.TAX },
        ...(this.experiments.enabled(
          SPECS.ecomPurchaseRecommendationsSettingsField,
        )
          ? [
              {
                requestedField:
                  RequestedFields.PURCHASE_RECOMMENDATIONS_SERVICE_PLUGIN,
              },
            ]
          : []),
      ],
    };
  }

  private getDeliveryConfiguration(
    ecommerceSettingsResponse: GetEcommerceSettingsResponse,
  ) {
    if (
      hasRequestedFieldError(
        ecommerceSettingsResponse.errors,
        RequestedFields.DELIVERY,
      )
    ) {
      return {
        countries: [],
        restOfWorld: { pickupOnly: false, postalCodeRequired: false },
      };
    }

    return {
      countries:
        ecommerceSettingsResponse.ecommerceSettings?.delivery?.countries,
      restOfWorld:
        ecommerceSettingsResponse.ecommerceSettings?.delivery?.restOfWorld,
    };
  }

  private async getEcommerceSettingsFromServerOrCache() {
    const cachedEcommerceSettings = this.viewerStorage.get();

    if (cachedEcommerceSettings) {
      return cachedEcommerceSettings;
    }

    const ecommerceSettings = (
      await this.errorHandler.withErrorHandler(
        () => this.ecommerceSettingsRequest(),
        { errorCodesMap: {} },
      )
    ).data;

    this.viewerStorage.set(ecommerceSettings);

    return ecommerceSettings;
  }

  private readonly ecommerceSettingsRequest = () => {
    const fields = [
      RequestedFields.TAX,
      RequestedFields.CHECKOUT,
      RequestedFields.BUSINESS_INFO,
      RequestedFields.DELIVERY,
      RequestedFields.PREMIUM_SITE,
    ];

    if (
      this.experiments.enabled(SPECS.ecomPurchaseRecommendationsSettingsField)
    ) {
      fields.push(RequestedFields.PURCHASE_RECOMMENDATIONS_SERVICE_PLUGIN);
    }

    return this.httpClient.request(getEcommerceSettings({ fields }));
  };

  private readonly checkoutSettingsRequest = () => {
    return this.httpClient.request(
      getEcommerceSettings({
        fields: [RequestedFields.CHECKOUT],
      }),
    );
  };
}
